<script>

import {axios, swal} from '../../mixins';
import MapBoxGl from '../../../../nova-components/Geometry/resources/js/components/MapBoxGl.vue';
import FarmerTotalsChart from "../../chart/FarmerDashTotalsChart.vue";
import FarmerDashBenchmarkingChart from "../../chart/FarmerDashBenchmarkingChart.vue";
import DashUpload from "./DashUpload.vue";

export default {

  name: 'FarmerDetails',

  props: {
    userId: {
      required: true,
      type: Number,
    },

    mapboxToken: {
      type: String,
      required: true,
    },

    range: {
      required: true,
      type: Object,
      default: [2000, 2030],
    },
  },

  components: {FarmerTotalsChart, FarmerDashBenchmarkingChart, MapBoxGl, DashUpload},

  mixins: [axios, swal],

  data() {
    return {
      user: {},
      fields: [],
      allFieldsVerified: null,
      benchmarkInfo: 'Empty',
      fieldIndex: -1,
      farmBoundary: null,
      years: null,
      contracts: null,
      filter: {
        year: null,
        contract: null,
      },
      // Loading message display
      loading: true,
      // In the case of no contracts found, display an error
      none: null,
      texts: {},
      tooltips: {},
      files: [],
      metrics: {
        contractId: null,
        acres: null,
        value: null,
        name: null,
        status: null,
        signed: null,
        readyToSign: null,
        hasPDF: null,
        estimateReady: null,
        perAcreValue: null,
        outcomes: null,
        nitrogenTotal: null,
        carbonTotal: null,
        phosphorousTotal: null,
        units: [
            'lbs',
            'lbs',
            'Mg'
        ],
        limitApplied: null,
        max: null,
      },
    };
  },

  computed: {
  },

  created() {
    this.getData()
  },

  methods: {
    getData(changeYear) {
      this.loading = true;
      if(changeYear === true) {
        this.filter.contract = null;
      }
      let route = '/user/' + this.userId + '?' + this.urlParams(this.filter);
      this.get(route).then((response) => {
        if (response.data.none) {
          this.none = true;
          this.loading = false;
          return;
        }
        if (response.data.metrics) {
          this.metrics = response.data.metrics;
        }
        if (response.data.benchmarkInfo) {
          this.benchmarkInfo = response.data.benchmarkInfo;
          this.$refs['chart-c-1'].setData(this.benchmarkInfo[1])
          this.$refs['chart-p-1'].setData(this.benchmarkInfo[2])
          this.$refs['chart-n-1'].setData(this.benchmarkInfo[3])
        }

        this.user = response.data.user;
        this.fields = response.data.fields;
        this.farmBoundary = response.data.farmBoundary;
        this.allFieldsVerified = response.data.allFieldsVerified;
        this.years = response.data.years;
        this.contracts = response.data.contracts;
        this.texts = response.data.tooltipTexts;
        for (let key in this.texts) {
          this.tooltips[key] = false;
        }
        for (const file of response.data.files) {
            this.files.push(file);
        }

        if(!this.filter.contract) {
          this.filter.contract = this.metrics.name;
          this.filter.year = this.filter.year ?? response.data.year;
        }
        this.loading = false;
        this.$refs.mapComponent.map.resize();
      }).catch((err) => {
      })
    },
    urlParams(myData) {
      let out = [];
      for (let key in myData) {
        if (myData.hasOwnProperty(key)) {
          if (myData[key] !== null) {
            out.push(key + '=' + encodeURIComponent(myData[key]));
          }
        }
      }
      return out.join('&');
    },
    setFieldIndex(value) {
      this.fieldIndex = value;
      if(this.fieldIndex === -1) {
        this.$refs.mapComponent.setMapBoundsToFit(this.farmBoundary);
      } else {
        this.$refs.mapComponent.setMapBoundsToFit(this.fields[this.fieldIndex]['boundary'])
      }
    },
    numberFormat(value) {
      const numberFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
      return numberFormat.format(value);
    },
    triggerUploadFileExplorer() {
      this.$refs.file.click();
    },
    doUpload(event) {
      let files = event.target.files;
      if (files.length !== 1) {
        return;
      }

      let data = new FormData();
      data.append('_token', this.csrf());
      data.append('upload', event.target.files[0]);

      this.swalLoading('', 'Uploading file...');

      this.post(this.url, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).then((response) => {
        this.file = response.data.content;

        this.swalClose();
        this.swalSuccess('', `Successfully uploaded file`);
      }).catch(() => {
        this.$refs.file.value = '';

        this.swalClose();
        this.swalError('', `Failed to upload file`);
      });
    },
    trimFileName(file)
    {
      const regex = /^[^-]*[^-]-/i;
      file = file.replace('tenants/1/contracts/' + this.metrics.contractId + '/', '');
      file = file.replace(regex, '');
      return file;
    }
  },
};

</script>

<template>
  <div class="light-mode w-2/3">
    <div class="flex justify-between">
      <div class="w-1/2 md:w-1/4 text-3xl text-center rounded-t-md bg-primary py-2 tab-shadow">
        Contract Review
      </div>
      <a class="bg-primary px-2 py-1 my-auto rounded tile-shadow border border-blue flex justify-center cursor-pointer hover:bg-grey-lighter"
         :href="`/?user_id=` + this.userId">
        <span><i class="fas fa-farm"></i></span>
        <span class="pl-1">Fields & Operations</span>
      </a>
    </div>
    <div v-show="this.loading" class="text-center text-2xl font-bold w-full mb-4 bg-primary rounded-tr rounded-b p-6 tile-shadow">Loading...</div>
    <div v-show="this.none" class="text-center text-2xl font-bold w-full mb-4 bg-primary rounded-tr rounded-b p-6 tile-shadow">No contracts found!</div>
    <div class="w-full mb-4 bg-primary rounded-tr rounded-b p-6 tile-shadow" v-show="!this.loading && !this.none">

      <!--  FILTER SELECTS  -->
      <div class="w-full flex flex-col mb-4">
          <div class="flex gap-2 my-auto w-full">
            <div>
              <label for="year" class="font-bold text-lg">Select Contract Year</label>
              <select name="year"
                      id="year"
                      v-model="filter.year"
                      @change="getData(true)"
                      class="md:col-start-4 bg-secondary italic border-b border-grey-light w-full py-05 px-3 py-2 rounded-md leading-tight focus:outline-none focus:ring">
                <option :value="null">Select Year</option>
                <option v-for="year in years" :value="year">{{ year }}</option>
              </select>
            </div>
            <div>
              <label for="contract" class="font-bold text-lg">Select Contract</label>
              <select name="contract"
                      id="contract"
                      v-model="filter.contract"
                      @change="getData(false)"
                      class="md:col-start-4 bg-secondary italic border-b border-grey-light w-full py-05 px-3 py-2 rounded-md leading-tight focus:outline-none focus:ring">
                <option :value="null">Select Contract</option>
                <option v-for="contract in contracts" :value="contract">{{ contract }}</option>
              </select>
            </div>
          </div>
        <div class="text-4xl text-left font-semibold">{{ this.metrics.name }}
        </div>
      </div>

      <!--  METRICS / MAP  -->
      <div class="grid grid-cols-1 md:grid-cols-2  gap-4 w-full mb-4">
        <div class="grid grid-cols-2 gap-2 order-2 md:order-1">

          <!-- CONTRACT STATUS -->
          <div class="flex flex-col bg-tertiary rounded-lg">
            <div class="bg-secondary rounded-t-lg px-2 py-1 font-semibold flex justify-between items-center">
              Contract Status
              <button v-on:mouseenter="tooltips['37'] = true" v-on:mouseleave="tooltips['37'] = false">
                <div class="relative">
                  <div :class="this.tooltips['37'] ? 'visible' : 'hidden'" class="w-96 bg-secondary p-2 border border-grey-light rounded absolute bottom-0 transform -translate-x-1/2">
                    <span v-html="this.texts['37']"></span>
                  </div>
                </div>
                <i class="fas fa-question-circle"></i>
              </button>
            </div>
            <div class="flex h-full">
              <div class="text-3xl m-auto p-2">
                {{ this.metrics.status }}
              </div>
            </div>
          </div>

          <!-- ACRES COUNT METRIC -->
          <div class="flex flex-col bg-tertiary rounded-lg">
            <div class="bg-secondary rounded-t-lg px-2 py-1 font-semibold">
              Contract Size
            </div>
            <div class="flex h-full">
              <div class="text-3xl m-auto p-2">
                {{ this.numberFormat(this.metrics.acres ? this.metrics.acres : '0') }} Acres
              </div>
            </div>
          </div>

          <!-- CONTRACT VALUE -->
          <div class="flex flex-col bg-tertiary rounded-lg">
            <div class="bg-secondary rounded-t-lg px-2 py-1 font-semibold flex justify-between items-center">
              Contract Value
              <button v-on:mouseenter="tooltips['39'] = true" v-on:mouseleave="tooltips['39'] = false">
                <div class="relative">
                  <div :class="this.tooltips['39'] ? 'visible' : 'hidden'" class="w-96 bg-secondary p-2 border border-grey-light rounded absolute bottom-0 transform -translate-x-1/2">
                    <span v-html="this.texts['39']"></span>
                  </div>
                </div>
                <i class="fas fa-question-circle"></i>
              </button>
            </div>
            <div class="flex h-full">
              <div class="text-3xl m-auto p-2">
                ${{ numberFormat(this.metrics.value) }}
              </div>
            </div>
          </div>

          <!-- CONTRACT PER ACRE VALUE -->
          <div class="flex flex-col bg-tertiary rounded-lg">
            <div class="bg-secondary rounded-t-lg px-2 py-1 font-semibold flex justify-between items-center">
              Contract Value per Acre
              <button v-on:mouseenter="tooltips['40'] = true" v-on:mouseleave="tooltips['40'] = false">
                <div class="relative">
                  <div :class="this.tooltips['40'] ? 'visible' : 'hidden'" class="w-96 z-50 bg-secondary p-2 border border-grey-light rounded absolute bottom-0 transform -translate-x-1/2">
                    <span v-html="this.texts['39']"></span>
                  </div>
                </div>
                <i class="fas fa-question-circle"></i>
              </button>
            </div>
            <div class="flex h-full">
              <div class="text-3xl m-auto p-2">
                ${{ this.metrics.perAcreValue ? this.metrics.perAcreValue : '0' }}
              </div>
            </div>
          </div>

          <!-- FILE DOWNLOADS -->
          <div class="md:col-span-2 flex flex-col bg-tertiary rounded-lg">
            <div class="bg-secondary rounded-t-lg px-2 py-2 font-semibold flex justify-between items-center">
              Documents
              <dash-upload :url="`/contracts/` + metrics.contractId + `/upload`" />
            </div>
            <div class="flex flex-col h-full">
              <div class="text-xl text-blue-light underline ml-2 p-2">
                <a :href="`/contracts/` + metrics.contractId + `/file`" v-if="this.metrics.hasPDF">
                  <p><i class="fas fa-file-pdf mr-2"></i>{{ metrics.name }} PDF</p>
                </a>
              </div>
              <div v-for="file in this.files">
                <div class="text-xl text-blue-light underline ml-2 p-2">
                  <a :href="`/contracts/` + metrics.contractId + `/download?file=` + file">
                    <p><i class="fas fa-file mr-2"></i>{{ this.trimFileName(file) }}</p>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- CONTRACT DOCUSIGN LINK -->
          <div class="flex col-span-2">
            <a v-if="this.metrics.readyToSign && !this.metrics.signed" :href="`/contracts/` + this.metrics.contractId + `/sign`" target="_self"
               class="w-full text-center py-1 rounded-sm text-white shadow-lg bg-green hover:bg-green-dark focus:bg-green-darker font-semibold"><i
                class="fas fa-download mr-1"></i> Review {{ this.filter.year }} Contract & Sign</a>
          </div>

          <!-- CONTRACT ESTIMATE LINK -->
          <div class="flex col-span-2">
            <a v-if="this.metrics.estimateReady" :href="`/contracts/` + this.metrics.contractId + `/estimate`" target="_self"
               class="w-full text-center py-1 rounded-sm text-white shadow-lg bg-green hover:bg-green-dark focus:bg-green-darker font-semibold"><i
                class="fas fa-download mr-1"></i> Review {{ this.filter.year }} Offer</a>
          </div>

          <!-- CONTRACT RE-ENROLLMENT OFFER -->
          <div class="flex col-span-2">
            <a v-if="this.metrics.enrollmentInfo && this.metrics.enrollmentInfo.offer" :href="`/contracts/` + this.metrics.contractId + `/re-enroll`" target="_self"
               class="w-full text-center py-1 rounded-sm text-white shadow-lg bg-green hover:bg-green-dark focus:bg-green-darker font-semibold"><i
                class="fas fa-download mr-1"></i>Re-enroll this farm for {{ this.metrics.enrollmentInfo.year }}</a>
          </div>

        </div>
        <div class="order-1 md:order-2" id="map" v-if="!this.loading && !this.none">
          <MapBoxGl
              :map-id="'map'"
              ref="mapComponent"
              :v-source="this.farmBoundary">
          </MapBoxGl>
        </div>
      </div>

      <!-- DETAIL LIST  -->
      <div class="w-full bg-secondary rounded-lg p-4 mb-4">
        <div class="flex flex-row mb-2">
          <h5 class="text-3xl text-left font-bold flex-1">Fields, Outcomes, and Payments</h5>
          <a :href="`/contracts/` + this.metrics.contractId + `/export-limited`" target="_blank"
             class="px-6 py-1 my-auto rounded-sm text-white shadow-lg bg-green hover:bg-green-dark focus:bg-green-darker font-semibold"><i
              class="fas fa-download mr-1"></i> Export
          </a>

        </div>
        <div class="h-96 overflow-y-scroll">
          <table class="rounded-t-lg w-full relative">
            <thead>
              <tr class="text-left">
                <th class="bg-primary sticky top-0 px-4 py-3">Field</th>
                <th class="bg-primary sticky top-0 px-4 py-3">Size (ac)</th>
                <th class="bg-primary sticky top-0 px-4 py-3 z-1000">Nitrogen Loss Prevented ({{ metrics.units[0] }}/ac/yr)
                  <button v-on:mouseenter="tooltips['41'] = true" v-on:mouseleave="tooltips['41'] = false">
                    <div class="relative overflow-visible">
                      <div :class="this.tooltips['41'] ? 'visible' : 'hidden'" class="w-96 bg-secondary p-2 border border-grey-light rounded absolute top-0 transform translate-y-10 -translate-x-1/2">
                        <span>Represents the total amount of nitrogen prevented from leaving enrolled fields measured in pounds per year</span>
                      </div>
                    </div>
                    <i class="fas fa-question-circle"></i>
                  </button>
                </th>
                <th class="bg-primary sticky top-0 px-4 py-3 z-1000">Phosphorous Loss Prevented ({{ metrics.units[1] }}/ac/yr)
                  <button v-on:mouseenter="tooltips['42'] = true" v-on:mouseleave="tooltips['42'] = false">
                    <div class="relative overflow-visible">
                      <div :class="this.tooltips['42'] ? 'visible' : 'hidden'" class="w-96 bg-secondary p-2 border border-grey-light rounded absolute top-0 transform translate-y-10 -translate-x-1/2">
                        <span>Represents the total amount of phosphorus prevented from leaving enrolled fields measured in pounds per year</span>
                      </div>
                    </div>
                    <i class="fas fa-question-circle"></i>
                  </button>
                </th>
                <th class="bg-primary sticky top-0 px-4 py-3 z-1000">Carbon ({{ metrics.units[2] }}/ac/yr)
                  <button v-on:mouseenter="tooltips['43'] = true" v-on:mouseleave="tooltips['43'] = false">
                    <div class="relative overflow-visible">
                      <div :class="this.tooltips['43'] ? 'visible' : 'hidden'" class="w-96 bg-secondary p-2 border border-grey-light rounded absolute top-0 transform translate-y-10 -translate-x-1/2">
                        <span>Represents the total amount of soil carbon sequestration and nitrous oxide reductions measured in metric tonnes per year</span>
                      </div>
                    </div>
                    <i class="fas fa-question-circle"></i>
                  </button>
                </th>
                <th class="bg-primary sticky top-0 px-4 py-3">Per Acre Payment</th>
                <th class="bg-primary sticky top-0 px-4 py-3">Total Payment</th>
                <th class="bg-primary sticky top-0 px-4 py-3">Verified</th>
              </tr>
            </thead>
            <template v-for="(field, index) in fields">
              <tr class="border-b border-grey-lighter hover:bg-grey-lightest" @click="setFieldIndex(index)" :class="{'bg-green-light': (index === fieldIndex)}">
                <td class="px-4 py-3">
                  {{ field.name }}
                </td>
                <td class="px-4 py-3">
                  {{ field.size }}
                </td>
                <td class="px-4 py-3">
                  {{ numberFormat(field.amounts[1] / field.size) }}
                </td>
                <td class="px-4 py-3">
                  {{ numberFormat(field.amounts[2] / field.size) }}
                </td>
                <td class="px-4 py-3">
                  {{ numberFormat(field.amounts[3] / field.size) }}
                </td>
                <td class="px-4 py-3">
                  ${{ numberFormat((this.metrics.allFieldsLimited ? (this.metrics.max * field.size) : field.limitedTotal) / field.size) }} <span v-show="field.max">**</span>
                </td>
                <td class="px-4 py-3">
                  ${{ this.metrics.allFieldsLimited ? numberFormat(this.metrics.max * field.size) : numberFormat(field.limitedTotal) }}
                </td>
                <td class="px-4 py-3 text-center text-xl">
                  <i :class="field.verified ? 'fas fa-check' : 'fas fa-times'"></i>
                </td>
              </tr>
            </template>
            <tfoot>
              <tr class="bg-primary font-bold sticky bottom-0 hover:bg-grey-lightest" @click="setFieldIndex(-1)" :class="{'bg-green-light': (-1 === fieldIndex)}">
                <td class="px-4 py-3">
                  Totals
                </td>
                <td class="px-4 py-3">
                  {{ numberFormat(metrics.acres) }}
                </td>
                <td class="px-4 py-3">
                  {{ numberFormat(metrics.nitrogenTotal / this.metrics.acres) }}
                </td>
                <td class="px-4 py-3">
                  {{ numberFormat(metrics.phosphorousTotal / this.metrics.acres) }}
                </td>
                <td class="px-4 py-3">
                  {{ numberFormat(metrics.carbonTotal / this.metrics.acres) }}
                </td>
                <td class="px-4 py-3">
                  ${{ numberFormat(metrics.value / metrics.acres) }}{{ this.metrics.limitApplied === 'max' ? '*' : ''}}
                </td>
                <td class="px-4 py-3">
                  ${{ numberFormat(metrics.value) }}{{ this.metrics.limitApplied === 'max' ? '*' : ''}}
                </td>
                <td class="px-4 py-3 text-center text-xl">
                  <div v-if="allFieldsVerified">
                    <i class="fas fa-check"></i>
                  </div>
                  <div v-else-if="!allFieldsVerified">
                    <i class="fas fa-times" title="Not all fields are verified."></i>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-if="metrics.limitApplied === 'max'" class="italic">
          * This contract is subject to a maximum of ${{ metrics.max }}/acre.
        </div>
        <div v-if="metrics.hasLimitedFields" class="italic">
          {{ metrics.allFieldsLimited ? `** All fields are limited due to the contract maximum of $` + metrics.max + `/acre.`
            : `** Field payment may be limited due to the contract maximum of $` + metrics.max +`/acre.` }}
        </div>
      </div>

      <div v-show="this.benchmarkInfo !== 'Empty'" class="w-full bg-secondary rounded-lg p-4">
        <h5 class="text-3xl text-left font-bold flex-1 col-span-12">Contract Benchmarking
          <button v-on:mouseenter="tooltips['38'] = true" v-on:mouseleave="tooltips['38'] = false">
            <div class="relative">
              <div :class="this.tooltips['38'] ? 'visible' : 'hidden'" class="text-lg w-96 z-50 bg-tertiary p-2 border border-grey-light rounded absolute bottom-0 transform -translate-x-1/2">
                <span v-html="this.texts['38']"></span>
              </div>
            </div>
            <i class="fas fa-question-circle text-xl"></i>
          </button>
        </h5>
        <div class="grid grid-cols-12 bg-primary items-center">
          <div class="text-xl text-center col-span-2">
            <span class="text-center">Nitrogen Loss Prevented (lbs/yr)</span>
          </div>
          <FarmerDashBenchmarkingChart
              class="col-span-10"
              :chart-id="'1'"
              :dataset-id-key="'data-1'"
              :height="75"
              cssClasses=""
              ref="chart-c-1"
          />
        </div>
        <div class="grid grid-cols-12 bg-primary items-center">
          <div class="text-xl text-center col-span-2">
            <span class="text-center">Phosphorous Loss Prevented (lbs/yr)</span>
          </div>
          <FarmerDashBenchmarkingChart
            class="col-span-10"
            :chart-id="'1'"
            :dataset-id-key="'data-1'"
            :height="75"
            cssClasses=""
            ref="chart-p-1"
          />
        </div>
        <div class="grid grid-cols-12 bg-primary rounded-b items-center">
          <div class="text-xl text-center col-span-2">
            <span class="text-center">Carbon MT/yr</span>
          </div>
          <FarmerDashBenchmarkingChart
            class="col-span-10"
            :chart-id="'1'"
            :dataset-id-key="'data-1'"
            :height="75"
            cssClasses=""
            ref="chart-n-1"
          />
        </div>
      </div>


    </div>
  </div>
</template>
